<template>
  <div
      class="flex flex-col items-center mx-4 mt-3 px-2"
      id="a4-receipt"
      :class="{
      'dir-ltr': $ctx.receiptLanguage == 'en',
      'dir-rtl': $ctx.receiptLanguage == 'ar'
    }"
  >
    <div
        class="flex flex-col items-center w-full a4-receipt-page"
        style="height: 100vh;"
    >
      <!-- header info -->
      <div id="a4-receipt-header" class="flex flex-col w-full">
        <div class="flex mt-4 justify-between items-center w-full">
          <div class="mx-8 mt-3 pb-2">
            <div class="flex items-center gap-2 mb-1">
              <img src="@/assets/icons/receipt/map-marker.svg" width="16" height="16" class="shrink-0" />
              <span>{{ settingsStore.getValue('company_address') }}</span>
            </div>
            <div class="flex items-center gap-2 mb-1">
              <img src="@/assets/icons/receipt/phone.svg" width="16" height="16" class="shrink-0" />
              <span dir="ltr">{{ settingsStore.getValue('company_phone1') + (settingsStore.getValue('company_phone2') ? ` - ${settingsStore.getValue('company_phone2')}` : '') }}</span>
            </div>
            <div class="flex items-center gap-6">
              <div v-if="settingsStore.getValue('company_email')" class="flex items-center gap-2">
                <img src="@/assets/icons/receipt/email.svg" width="16" height="16" class="shrink-0" />
                <span>{{ settingsStore.getValue('company_email') }}</span>
              </div>
              <div v-if="settingsStore.getValue('company_website')" class="flex items-center gap-2">
                <img src="@/assets/icons/receipt/web.svg" width="16" height="16" class="shrink-0" />
                <span>{{ settingsStore.getValue('company_website') }}</span>
              </div>
            </div>
          </div>

          <div class="mx-8">
            <img
                :src="settingsStore.getValue('receipt_logo') ? settingsStore.storagePath + '/' + settingsStore.getValue('receipt_logo'): '/images/logo.svg'"
                :width="settingsStore.getValue('receipt_logo_width')"
                :style="{'margin-top': settingsStore.getValue('receipt_logo_top_margin') + 'px', 'margin-right': settingsStore.getValue('receipt_logo_right_margin') + 'px'}"
                alt=""/>
          </div>
        </div>

        <div class="flex flex-col w-full items-center">
          <div class="w-11/12 h-[3px] rounded-full"
              :style="{background: `linear-gradient(to right, transparent, ${settingsStore.getValue('receipt_branding_color') || '#585954'}, transparent)`}"></div>
        </div>
      </div>

      <div id="a4-receipt-body" class="w-full">
        <!-- title -->
        <div class="flex flex-col w-full mt-2 items-center">
          <h2 class="bg-gray-200 rounded py-1 px-16 font-bold mt-3 capitalize">
            <p>
              {{
                receiptTranslate("Transactions Receipt", "receipt.transactionReceipt")
              }}
            </p>
          </h2>
        </div>

        <!-- order type info and barcode -->
        <div class="flex w-full justify-around">
          <div>
            <table v-if="transaction" class="w-full text-sm mt-2">
              <tr>
                <td>
                  <p>{{ receiptTranslate("Type", "receipt.type") }}</p>
                </td>
                <td>
                  <p class="font-bold mx-5">
                    {{
                      receiptTranslate("Collective", "receipt.collective")
                    }}
                  </p>
                </td>
              </tr>
              <tr>
                <td>
                  <p>
                    {{ receiptTranslate("Location", "receipt.location") }}
                  </p>
                </td>
                <td>
                  <p class="font-bold mx-5">
                    {{ transaction?.location?.enName || "" }}
                  </p>
                </td>
              </tr>
              <tr class="align-baseline">
                <td>
                  <p>
                    {{ receiptTranslate("Order No", "receipt.orderNo") }}
                  </p>
                </td>
                <td>
                  <div class="font-bold mx-5 break-after-all w-[200px]">{{
                      transaction?.children?.map(c => c.referenceableExternalID).filter((x, i, a) => a.indexOf(x) == i).join(', ')
                    }}</div>
                </td>
              </tr>
              <tr v-if="settingsStore.getValue('hide_cashier_name_in_receipt') != 'true'">
                <td>
                  <p>
                    {{ receiptTranslate("Cashier", "receipt.cashier") }}
                  </p>
                </td>
                <td>
                  <p class="font-bold mx-5">
                    {{ transaction?.user?.fullName }}
                  </p>
                </td>
              </tr>
              <tr>
                <td>
                  <p>
                    {{ receiptTranslate("Date & Time", "receipt.dateAndTime") }}
                  </p>
                </td>
                <td>
                  <p class="font-bold mx-5" dir="ltr">
                    {{ parseFormat(transaction?.createdAt) }}
                  </p>
                </td>
              </tr>
              <tr>
                <td>
                  <p>
                    {{
                      receiptTranslate("Payment Notes", "receipt.paymentNotes")
                    }}
                  </p>
                </td>
                <td>
                  <p class="font-bold mx-5" dir="ltr">
                    {{ transaction?.description }}
                  </p>
                </td>
              </tr>
            </table>
          </div>

          <div class="flex flex-col items-center">
            <div>
              <svg :id="`barcode`" class="mt-3"></svg>
              <div class="flex items-center justify-center">
                <span class="font-bold mx-5" style="font-size: calc(20px * var(--receipt-font-scale, 1));">
                  {{ transaction?.externalID?.slice(0, 5) }}-{{
                    transaction?.externalID?.slice(5)
                  }}
                </span>
              </div>
            </div>
            <div
                v-if="transaction?.customerInvoice?.pouchID"
                class="flex flex-col w-full mt-2 items-center"
            >
              <h2 class="bg-gray-200 rounded py-1 px-2 flex">
                <span>{{ receiptTranslate("Pouch #", "receipt.pouch") }}</span>
                <span class="flex justify-end custom-bold mx-2">
                  {{ transaction?.customerInvoice?.pouchID }}
                </span>
              </h2>
            </div>
          </div>
        </div>

        <!-- payment info -->
        <div class="text-sm flex flex-col w-full items-end">
          <div class="flex-col w-5/12 mr-10">
            <div class="w-full p-2"></div>

            <div class="px-4 w-full mt-2">
              <div>
                <table
                    class="payment-table"
                    :class="{ 'ar-receipt': $ctx.receiptLanguage == 'ar' }"
                >
                  <tr>
                    <td>
                      <p class="p-0">
                        {{
                          receiptTranslate("Payment ID", "receipt.paymentID")
                        }}: {{ transaction?.externalID }}
                      </p>
                    </td>
                    <td>
                      <p class="text-right">
                        {{ parseFormat(transaction?.createdAt) }}
                      </p>
                    </td>
                  </tr>
                </table>

                <table
                    class="payment-table"
                    :class="{ 'ar-receipt': $ctx.receiptLanguage == 'ar' }"
                >
<!--                  <tr v-if="transaction?.receiptType != 'return'">-->
<!--                    <td>-->
<!--                      {{-->
<!--                        receiptTranslate(-->
<!--                            "Payment Method",-->
<!--                            "receipt.paymentMethod"-->
<!--                        )-->
<!--                      }}-->
<!--                    </td>-->
<!--                    <td>-->
<!--                      <p class="text-right">-->
<!--                        {{-->
<!--                          transaction?.paymentMethod === "debit-card"-->
<!--                              ? "E-Payment"-->
<!--                              : "Cash"-->
<!--                        }}-->
<!--                      </p>-->
<!--                    </td>-->
<!--                  </tr>-->

<!--                  <tr-->
<!--                      v-if="-->
<!--                      transaction?.receiptType != 'return' &&-->
<!--                        transaction?.paymentMethod === 'cash'-->
<!--                    "-->
<!--                  >-->
<!--                    <td>{{ receiptTranslate("Cash", "receipt.cash") }}</td>-->
<!--                    <td>-->
<!--                      <p class="text-right">-->
<!--                        {{ formatDinero(transaction?.paidBeforeChangeDinero) }}-->
<!--                      </p>-->
<!--                    </td>-->
<!--                  </tr>-->

                  <tr
                      v-if="transaction?.multiCurrencyBreakdown?.length === 2 && transaction?.multiCurrencyBreakdown[1].amount > 0"
                  >
                    <td>
                      {{
                        receiptTranslate("Payment Currencies", "receipt.paymentCurrencies")
                      }}
                    </td>
                    <td>
                      <p
                          class="text-right"
                      >
                        {{ formatMoney(transaction?.multiCurrencyBreakdown[0].amount, transaction?.multiCurrencyBreakdown[0].currency) }}
                        +
                        {{ formatMoney(transaction?.multiCurrencyBreakdown[1].amount, transaction?.multiCurrencyBreakdown[1].currency) }}
                      </p>
                    </td>
                  </tr>

                  <tr
                      v-if="
                      transaction?.receiptType != 'return' &&
                        transaction?.paymentMethod === 'cash'
                    "
                  >
                    <td>
                      {{ receiptTranslate("Change Due", "receipt.changeDue") }}
                    </td>
                    <td>
                      <p class="text-right">
                        {{ formatDinero(transaction?.changeDueDinero) }}
                      </p>
                    </td>
                  </tr>
                  <tr>
                    <td>
                      {{ receiptTranslate("Total Paid", "receipt.totalPaid") }}
                    </td>
                    <td>
                      <p
                          v-if="transaction?.receiptType != 'return'"
                          class="text-right"
                      >
                        {{ formatDinero(transaction?.amountDinero) }}
                      </p>
                      <p
                          v-if="transaction?.receiptType == 'return'"
                          class="text-right"
                      >
                        ({{ formatDinero(transaction?.amountDinero) }})
                      </p>
                    </td>
                  </tr>

<!--                  <tr v-if="transaction?.receiptType != 'return'">-->
<!--                    <td>-->
<!--                      {{ receiptTranslate("Balance", "receipt.balance") }}-->
<!--                    </td>-->
<!--                    <td>-->
<!--                      <p class="text-right">-->
<!--                        {{ formatDinero(transaction?.balanceDinero) }}-->
<!--                      </p>-->
<!--                    </td>-->
<!--                  </tr>-->
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
          id="a4-receipt-footer"
          v-if="settingsStore.getValue('show_receipts_thecky_footer') == 'true'"
          class="flex justify-center self-center w-full mt-auto"
      >
        <div
            class="flex items-center justify-between py-4 px-4 mt-2"
            style="bottom: 0px; width: 95%; border-top: 2px solid #BDC7D6"
        >
          <p><img src="@/assets/images/thecky-qr.png" style="height: 25px;"/></p>
          <p><img src="@/assets/images/footer.svg" style="height: 25px;"/></p>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import {defineComponent, ref} from "vue";

import {i18nOrderMessages} from "@/models/sales/Order";
import {parseFormat} from "@/utils/date";
import {useI18n} from "vue-i18n";
import {formatDinero, formatMoney} from "@/utils/money";
import {Transaction} from "@/models/accounting/Transaction";
import JsBarcode from "jsbarcode";
import transaction from "../../accounting/transactions/Transaction.vue";
import {useSettingsStore} from "@/stores/settingsStore";
import { receiptPrintCSS } from "@/utils/receiptPrint";

export default defineComponent({
  components: {},

  setup() {
    const {t} = useI18n({
      messages: {
        en: {
          ...i18nOrderMessages.en,
        },
        ar: {
          ...i18nOrderMessages.ar,
          receipt: {
            salesReceipt: "فاتورة بيع",
            paymentReceipt: "إيصال الدفع",
            returnReceipt: "إيصال الارجاع",
            transactionReceipt: 'فاتورة مدفوعات',
            type: "نوع الطلب",
            direct: "مباشر",
            credit: "آجل",
            layaway: "عربون",
            customer: "الزبون",
            cashCustomer: "زبون",
            location: "المتجر",
            orderNo: "رقم الطلب",
            cashier: "موظف الكاشير",
            dateAndTime: "التاريخ والوقت",
            orderNotes: "ملاحظات الطلب",
            itemName: "اسم المنتج",
            qty: "الكمية",
            price: "السعر",
            finalPrice: "السعر النهائي",
            totalBeforeDiscounts: "المبلغ الكلي قبل التخفيض",
            totalDiscount: "المبلغ الكلي للتخفيض",
            totalDue: "الاجمالي",
            totalItemsSold: "عدد المنتجات المباعة",
            transactionsInfo: "الحركات المالية",
            paymentID: "رقم الحركة",
            paymentMethod: "طريقة الدفع",
            cash: "كاش",
            changeDue: "المتبقي",
            totalPaid: "المبلغ المدفوع الكلي",
            balance: "المتبقي",
            paymentNotes: "ملاحظات الحركة",
            collective: "دفع جماعي"
          }
        }
      }
    });

    const settingsStore = useSettingsStore();
    const transaction = ref<Transaction>();

    return {t, settingsStore, transaction};
  },
  props: {
    transactionData: {
      type: Transaction,
      required: true
    }
  },
  mounted() {
    this.transaction = Transaction.from(this.transactionData)
    setTimeout(() => {
      JsBarcode(
          `#barcode`,
          String(this.transaction?.externalID!.slice()),
          {
            height: 50,
            displayValue: false
          }
      );

      for (const tx of this.transaction.children) {
        this.transaction.totalDueAmount += tx.totalDueAmount;
        this.transaction.totalPaidAmount += tx.totalPaidAmount;
        this.transaction.changeDueAmount += tx.changeDueAmount;
        this.transaction.amountAmount += tx.amountAmount;
      }

      this.transaction = Transaction.from(this.transaction)

    }, 100);
  },
  created() {
    // Add print CSS when component is created
    this.addPrintCSS();
  },
  beforeUnmount() {
    // Remove print CSS when component is about to be destroyed
    this.removePrintCSS();
  },
  methods: {
    parseFormat,
    formatDinero,
    formatMoney,
    truncateItemName(itemName: string) {
      const maxLength = Math.min(itemName.length, 40);
      return maxLength >= 40
          ? `${itemName.substring(0, maxLength)}...`
          : itemName;
    },
    print() {
      window.print();
    },
    addPrintCSS() {
      // Create a style element with a unique id
      const style = document.createElement("style");
      style.setAttribute("id", "print-css");
      style.textContent = receiptPrintCSS(this.$ctx.getReceiptSize());
      // Append the style to the document head
      document.head.appendChild(style);
    },
    removePrintCSS() {
      // Find and remove the style element
      const style = document.querySelector("#print-css");
      if (style && style.parentNode) {
        style.parentNode.removeChild(style);
      }
    },
    receiptTranslate(value: string, translateKey: string): string {
      return this.$ctx.receiptLanguage == "en"
          ? value
          : this.t(translateKey, this.$ctx.receiptLanguage);
    }
  }
});
</script>
<style>
.thecky-site {
  color: #000;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  letter-spacing: 3px;
}

#a4-receipt {
  width: 210mm;
  overflow: scroll;
  background-color: #fff;
}

@media print {
  body * {
    page-break-after: avoid;
    visibility: hidden;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }

  #a4-receipt,
  #a4-receipt * {
    visibility: visible;
  }

  #a4-receipt {
    display: block;
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    overflow: visible;
    margin: 0cm;
  }

  #a4-receipt-header {
    top: 0;
  }

  #a4-receipt-body {
    position: relative;
  }

  p,
  div {
    page-break-inside: avoid;
  }
}
</style>
